import React, {useLayoutEffect, useContext} from 'react';
import {
  Switch,
} from 'react-router-dom';
import { useLocation } from 'react-router';
import { useTransition, animated } from 'react-spring';
import delay from '../utils/delay';
import styled, {css} from 'styled-components';
import flattenFragment from '../utils/flattenFragment';
import EnvironmentVariablesContext
  from '../contexts/EnvironmentVariablesContext';

export default function AnimatedSwitch({
  children,
}) {
  const location = useLocation();
  const environmentVariables = useContext(EnvironmentVariablesContext);

  useLayoutEffect(() => {
    const fixWindowHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      const meta = document.querySelector('meta[name="viewport"]');
      meta.content = 'width=device-width,height='+window.innerHeight+',initial-scale=1.0,maximum-scale=1.0,user-scalable=0';
    }

    window.addEventListener('resize', fixWindowHeight);
    fixWindowHeight();

    return () => window.removeEventListener('resize', fixWindowHeight);
  }, [])

  const transitions = useTransition(location, location => location.pathname, {
    config: item => {
      const duration = (
        item.state !== undefined &&
        item.state.duration !== undefined
      )
        ? item.state.duration
        : parseInt(environmentVariables.REACT_APP_PAGE_TRANSITION_MS);

      return ({
        precision: 0.1,
        duration,
      });
    },
    from: { opacity: 0 },
    enter: item => async (next, cancel) => {
      const ms = item.state !== undefined && item.state.delay !== undefined
        ? item.state.delay
        : 0;

      await delay(ms);

      await next({ opacity: 1 });
    },
    leave: { opacity: 0 },
    unique: true,
  });

  return transitions.map(({ location, props, key }) => (
    <Container key={key} style={props}>
      <Switch location={location}>
        {flattenFragment(children)}
      </Switch>
    </Container>
  ));
}

const Container = styled(animated.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  ${props => props.theme.deviceInfo.isMobile && props.theme.deviceInfo.portrait && css`
    min-height: calc(var(--vh, 1vh) * 100);
  `}
  ${props => props.theme.deviceInfo.isMobile && css`
    overflow-y: auto;
  `}
`;
